import React, { useState } from 'react';
import { GROUP_TYPE_CFG } from './GroupsManagement';

// Voyager Pro reskin (Figma GroupsPage) - form kept on the real packages /
// payment_schedule / coordinator data model (multi-package pricing is real
// functionality Figma's mock didn't show but agents actively use - 10 real
// group bookings rely on it), just restyled to match Figma's visual language.

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5";

const GroupForm = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    destination: '',
    start_date: '',
    end_date: '',
    component_type: 'group_tour',
    max_participants: '',
    notes: '',
    packages: [{ id: 1, name: '', description: '', price: '', inclusions: [] }],
    payment_schedule: [{ id: 1, name: 'Deposit', percentage: 30, due_date: '' }],
    coordinator: { name: '', email: '', phone: '' },
    cancellation_policy: '',
  });
  const [currentInclusion, setCurrentInclusion] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.destination || !formData.start_date || !formData.end_date) {
      alert('Please fill in all required fields');
      return;
    }
    if (!formData.packages[0].name || !formData.packages[0].price) {
      alert('Please add at least one package with name and price');
      return;
    }
    onSubmit(formData);
  };

  const handlePackageChange = (index, field, value) => {
    const updated = [...formData.packages];
    updated[index][field] = value;
    setFormData({ ...formData, packages: updated });
  };

  const addPackage = () => setFormData({
    ...formData,
    packages: [...formData.packages, { id: formData.packages.length + 1, name: '', description: '', price: '', inclusions: [] }],
  });

  const removePackage = (index) => {
    if (formData.packages.length > 1) {
      setFormData({ ...formData, packages: formData.packages.filter((_, i) => i !== index) });
    }
  };

  const addInclusion = (packageIndex) => {
    if (!currentInclusion.trim()) return;
    const updated = [...formData.packages];
    updated[packageIndex].inclusions.push(currentInclusion.trim());
    setFormData({ ...formData, packages: updated });
    setCurrentInclusion('');
  };

  const removeInclusion = (packageIndex, inclusionIndex) => {
    const updated = [...formData.packages];
    updated[packageIndex].inclusions.splice(inclusionIndex, 1);
    setFormData({ ...formData, packages: updated });
  };

  const handlePaymentScheduleChange = (index, field, value) => {
    const updated = [...formData.payment_schedule];
    updated[index][field] = value;
    setFormData({ ...formData, payment_schedule: updated });
  };

  const addPaymentSchedule = () => setFormData({
    ...formData,
    payment_schedule: [...formData.payment_schedule, { id: formData.payment_schedule.length + 1, name: '', percentage: 0, due_date: '' }],
  });

  const removePaymentSchedule = (index) => {
    if (formData.payment_schedule.length > 1) {
      setFormData({ ...formData, payment_schedule: formData.payment_schedule.filter((_, i) => i !== index) });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Create Group Booking</h2>
          <p className="text-xs text-[var(--muted-foreground)] mt-0.5">Set up a group trip agents can share a signup link for.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6 overflow-y-auto">
        {/* Basic Information */}
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-3">Basic Information</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>Group Title *</label>
              <input className={inputCls} value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Caribbean Cruise Adventure 2027" required />
            </div>
            <div>
              <label className={labelCls}>Destination *</label>
              <input className={inputCls} value={formData.destination} onChange={e => setFormData({ ...formData, destination: e.target.value })} placeholder="e.g., Eastern Caribbean" required />
            </div>
            <div>
              <label className={labelCls}>Group Type *</label>
              <select className={inputCls} value={formData.component_type} onChange={e => setFormData({ ...formData, component_type: e.target.value })} required>
                {Object.entries(GROUP_TYPE_CFG).map(([value, cfg]) => <option key={value} value={value}>{cfg.icon} {cfg.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Start Date *</label>
              <input type="date" className={inputCls} value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} required />
            </div>
            <div>
              <label className={labelCls}>End Date *</label>
              <input type="date" className={inputCls} value={formData.end_date} onChange={e => setFormData({ ...formData, end_date: e.target.value })} required />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Max Participants</label>
              <input type="number" min="1" className={inputCls} value={formData.max_participants} onChange={e => setFormData({ ...formData, max_participants: e.target.value })} placeholder="Leave blank for unlimited" />
            </div>
          </div>
        </div>

        {/* Packages */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest">Packages</h3>
            <button type="button" onClick={addPackage} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">+ Add Package</button>
          </div>
          <div className="space-y-3">
            {formData.packages.map((pkg, index) => (
              <div key={pkg.id} className="p-3.5 bg-[var(--muted)]/40 rounded-xl border border-[var(--border)]">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="text-xs font-bold text-[var(--foreground)]">Package {index + 1}</div>
                  {formData.packages.length > 1 && (
                    <button type="button" onClick={() => removePackage(index)} className="text-xs font-semibold px-2 py-0.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <input className={inputCls} placeholder="Package name" value={pkg.name} onChange={e => handlePackageChange(index, 'name', e.target.value)} required />
                  <input type="number" min="0" step="0.01" className={inputCls} placeholder="Price per person" value={pkg.price} onChange={e => handlePackageChange(index, 'price', e.target.value)} required />
                  <textarea rows={2} className={`${inputCls} col-span-2`} placeholder="Package description…" value={pkg.description} onChange={e => handlePackageChange(index, 'description', e.target.value)} />
                  <div className="col-span-2">
                    <div className="flex gap-2 mb-2">
                      <input
                        className={inputCls}
                        placeholder="Add inclusion…"
                        value={currentInclusion}
                        onChange={e => setCurrentInclusion(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInclusion(index); } }}
                      />
                      <button type="button" onClick={() => addInclusion(index)} className="px-3.5 py-2.5 rounded-xl text-xs font-semibold bg-white border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)] flex-shrink-0">Add</button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {pkg.inclusions.map((inc, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700">
                          {inc}
                          <button type="button" onClick={() => removeInclusion(index, i)} className="text-sky-400 hover:text-sky-700">×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Schedule */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest">Payment Schedule</h3>
            <button type="button" onClick={addPaymentSchedule} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">+ Add Installment</button>
          </div>
          <div className="space-y-2">
            {formData.payment_schedule.map((p, index) => (
              <div key={p.id} className="grid grid-cols-[1fr,90px,140px,auto] gap-2 items-center">
                <input className={inputCls} placeholder="e.g. Deposit" value={p.name} onChange={e => handlePaymentScheduleChange(index, 'name', e.target.value)} />
                <input type="number" min="0" max="100" className={inputCls} value={p.percentage} onChange={e => handlePaymentScheduleChange(index, 'percentage', parseInt(e.target.value) || 0)} />
                <input type="date" className={inputCls} value={p.due_date} onChange={e => handlePaymentScheduleChange(index, 'due_date', e.target.value)} />
                {formData.payment_schedule.length > 1 && (
                  <button type="button" onClick={() => removePaymentSchedule(index)} className="text-xs font-semibold px-2 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Remove</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Coordinator */}
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-3">Group Coordinator (optional)</h3>
          <div className="grid grid-cols-3 gap-2.5">
            <input className={inputCls} placeholder="Name" value={formData.coordinator.name} onChange={e => setFormData({ ...formData, coordinator: { ...formData.coordinator, name: e.target.value } })} />
            <input className={inputCls} placeholder="Email" value={formData.coordinator.email} onChange={e => setFormData({ ...formData, coordinator: { ...formData.coordinator, email: e.target.value } })} />
            <input className={inputCls} placeholder="Phone" value={formData.coordinator.phone} onChange={e => setFormData({ ...formData, coordinator: { ...formData.coordinator, phone: e.target.value } })} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Cancellation Policy</label>
          <textarea rows={2} className={inputCls} value={formData.cancellation_policy} onChange={e => setFormData({ ...formData, cancellation_policy: e.target.value })} />
        </div>

        <div>
          <label className={labelCls}>Agent Notes</label>
          <textarea rows={2} className={inputCls} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Internal notes - never shown to participants…" />
        </div>
      </form>

      <div className="flex gap-3 px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
        <button type="button" onClick={handleSubmit} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--primary)' }}>Create Group Booking</button>
      </div>
    </>
  );
};

export default GroupForm;
